import { useQuery } from "react-query";
import { request } from "../../config/helpers/axios-instance";
import { throwIfRequestError } from "../../v2/hooks/request-error";

// Order Tracking Sheet — pillup-api controller/ops/trackingSheet.js
// (GET /api/ops/tracking-sheet/{columns,orders,coverage,export}).
//
// Same envelope rule as useTatOrders: every pillup-api failure comes back as HTTP 200
// with `success: false`, which throwIfRequestError cannot see, so each fn checks
// `success` itself to land react-query in a real error state.

const BASE = "ops/tracking-sheet";

async function get(path, params) {
    const result = throwIfRequestError(await request({ url: `${BASE}/${path}`, method: "GET", params }));
    const body = result.data;
    if (!body || body.success === false) {
        throw new Error(body?.error?.message || body?.message || "Request failed");
    }
    return body.data;
}

/** Drops empty values so the URL and cache key only carry what was actually chosen. */
export function buildParams({ from, to, view, search, source, deliveryStatus, sort } = {}) {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (view) params.view = view;
    if (search) params.search = search;
    if (source) params.source = source;
    if (deliveryStatus) params.deliveryStatus = deliveryStatus;
    if (sort && sort !== "recent") params.sort = sort;
    return params;
}

export function useTrackingColumns() {
    return useQuery(["tracking-sheet", "columns"], () => get("columns"), {
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });
}

export function useTrackingOrders(filters, { page, size }) {
    const params = { ...buildParams(filters), page, size };
    return useQuery(["tracking-sheet", "orders", params], () => get("orders", params), {
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });
}

export function useTrackingCoverage(filters, { enabled }) {
    const params = buildParams(filters);
    return useQuery(["tracking-sheet", "coverage", params], () => get("coverage", params), {
        enabled,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

/** Imperative: the export button fetches every row in range once, on click. */
export function fetchExportRows(filters) {
    return get("export", buildParams(filters));
}
